import RateDisplay from "@components/Cards/RateDisplay";
import { useFormatContext } from "@lib/hooks/format-context";
import { CellContext, HeaderContext } from "@tanstack/react-table";
import React from "react";
import { getTotalValueOfAllRows, getCorrelation } from "./calculations";

type Props<T> = {
  compared?: boolean;
} & (
  | {
      info: CellContext<T, number>;
      calculateTotals?: false;
      total?: never;
      comparedTotal?: never;
    }
  | {
      info: HeaderContext<T, number>;
      calculateTotals: true;
      total?: never;
      comparedTotal?: never;
    }
  | {
      total: number;
      comparedTotal?: number;
      info?: never;
      calculateTotals?: never;
    }
);

export type CurrencyCalculatorType = (
  value: number,
  numDigits?: number
) => string;

const getDisplayString = (
  value: number,
  formatNumber: CurrencyCalculatorType
) => {
  return formatNumber(value, 0);
};

function PageviewTableDisplay<T extends Record<string, any>>({
  info,
  compared,
  calculateTotals,
  total,
  comparedTotal,
}: Props<T>) {
  const { formatNumber } = useFormatContext();
  let comparedValue = 0;
  let value = 0;

  if (total !== undefined) {
    value = total;
    comparedValue = comparedTotal ?? 0;
  } else if (calculateTotals) {
    const allValues = info.column.getFacetedRowModel().rows.map((row) => {
      return {
        value: row.original?.[info.column.id],
        compared: row.original?.compared?.[info.column?.id],
      };
    });
    const totals = getTotalValueOfAllRows({
      values: allValues,
      roundBeforeAdding: true,
    });

    value = totals.value;
    comparedValue = totals.compared ?? 0;
  } else if (info) {
    value = info.getValue();
    if (compared && info.row.original?.compared) {
      comparedValue = info.row.original?.compared[info.column?.id];
    }
  }

  if (compared) {
    const rate = getCorrelation(value, comparedValue);
    return (
      <div className="flex items-center w-full">
        <div className="flex-1">
          <p>{getDisplayString(value, formatNumber)}</p>
          <p className="text-[11px] leading-tight opacity-70">
            vs {getDisplayString(comparedValue, formatNumber)}
          </p>
        </div>
        <RateDisplay rate={rate} isIncreasing={rate > 0} size="small" />
      </div>
    );
  }
  return getDisplayString(value, formatNumber);
}

export default PageviewTableDisplay;
